import { memo } from "react";

import { Button, Row, Dialog } from "@narmi/design_system";
import { useLocalStorage } from "usehooks-ts";
import { Localized, useLocalization } from "@fluent/react";
import { entities } from "byzantine";

import styles from "./SidebarUpdateCard.module.scss";
import { DAYS_UNTIL_FORCED_INTO_SIDEBAR } from "./constants";

const { useUpdateUserMetadata } = entities;

const SidebarUpdateCardDialog = ({
  isOpen,
  closeDialog,
}: {
  isOpen: boolean;
  closeDialog: () => void;
}) => {
  const { l10n } = useLocalization();
  const { editUserMetadata } = useUpdateUserMetadata();
  const [optedOutCount, setOptedOutCount] = useLocalStorage<number>(
    "sidebarUpdateCardOptedOutCount",
    0
  );
  const [_isCardConfirmed, setUpdateCardConfirmation] =
    useLocalStorage<boolean>("sidebarUpdateCardConfirmed", false);

  function revertToTopMenu() {
    closeDialog();
    const date = new Date();
    date.setDate(date.getDate() + DAYS_UNTIL_FORCED_INTO_SIDEBAR);
    document.cookie = `opt_out_of_olb_sidebar_navigation=true; expires=${date.toUTCString()}; path=/`;
    // Set the local storage value to keep track of how many times they've opted out
    setOptedOutCount((optedOutCount ?? 0) + 1);
    editUserMetadata({
      sidebar_opt_out_expiration_date: date.toISOString(),
    });
    // If we'd serve any Django-rendered HTML differently based on this cookie we must reload
    window.location.reload();
  }

  const isAbleToOptOut = optedOutCount === 0;

  return (
    <Dialog
      isOpen={isOpen}
      onUserDismiss={closeDialog}
      title={l10n.getString(
        "update-card-dialog-title",
        null,
        "A more focused dashboard"
      )}
      footer={
        <div
          className={styles["sidebar-update-card-modal-footer"]}
          role="group"
        >
          <Row alignItems="center" justifyContent="end">
            {isAbleToOptOut ? (
              <Row.Item shrink>
                <Button
                  testId="sidebar-update-card-dialog-opt-out"
                  onClick={revertToTopMenu}
                  kind="negative"
                  label={l10n.getString(
                    "update-card-dialog-opt-out-button",
                    null,
                    "Go back to top menu"
                  )}
                />
              </Row.Item>
            ) : null}
            <Row.Item shrink>
              <Button
                testId="sidebar-update-card-dialog-confirm"
                onClick={() => {
                  closeDialog();
                  setUpdateCardConfirmation(true);
                }}
                kind="primary"
                label={l10n.getString(
                  "update-card-dialog-confirm-button",
                  null,
                  "Keep side menu"
                )}
              />
            </Row.Item>
          </Row>
        </div>
      }
    >
      <p className={styles["sidebar-update-card-modal-body"]}>
        <Localized id="update-card-dialog-body">
          To help you find what you need faster, we&apos;ve moved the top menu
          to the side. Every tool you use is still here—now with more room to
          give you a clearer view of your finances.
        </Localized>
        {isAbleToOptOut ? (
          <>
            <br />
            <br />
            <p>
              <Localized id="update-card-dialog-body-opt-out">
                If you aren&apos;t ready for this new look, you can go back to
                the top menu for a limited time.
              </Localized>
            </p>
          </>
        ) : null}
      </p>
    </Dialog>
  );
};

export default memo(SidebarUpdateCardDialog);
